import {Pagination} from "react-bootstrap";
import React from "react";
import {useDispatch, useSelector} from "react-redux";

// This is a customized pagination component that seamlessly pages between two lists
// of claimed gift cards - the modern claimed gift cards and the legacy claimed gift cards.
//
// It requires the following props:
// currentPage - the current page number
// pageSize - the number of items per page
// totalModernPages - the total number of pages for modern claimed gift cards
// totalLegacyPages - the total number of pages for legacy claimed gift cards
// modernPageFetchHandler - the function to dispatch when a new modern page is required
// legacyPageFetchHandler - the function to dispatch when a new legacy page is required
// prevPageHandler - the function to call to update the state when the previous page button is clicked
// nextPageHandler - the function to call to update the state when the next page button is clicked
const CombinedOffersPaginationComponent = ({currentPage, pageSize, totalModernPages, totalLegacyPages,
                               modernPageFetchHandler, legacyPageFetchHandler,
                               prevPageHandler, nextPageHandler, ...rest}) => {

  const dispatch = useDispatch();

  // Fetch the actual page numbers that are currently in the Redux store
  const currentModernPage = useSelector((state) =>
    state.registeredUserDetails.userData.claimedGiftCards.page.number);
  const currentLegacyPage = useSelector((state) =>
    state.registeredUserDetails.userData.claimedLegacyGiftCards.page.number);

  // Determine if the current page is in the modern or legacy range
  const isModernPage = currentPage < totalModernPages;

  // Calculate the real page index based on the current page type
  const realPageIndex = isModernPage ? currentPage : currentPage - totalModernPages;

  const fetchPrevPage = () => {
    if (isModernPage) {
      // Only request the previous modern page if we are not already at the first modern page
      if (realPageIndex !== 0) {
        dispatch(modernPageFetchHandler(realPageIndex - 1, pageSize));
      }
    } else {
      // Only request the previous legacy page if we are not already at the first legacy page
      if (realPageIndex !== 0) {
        dispatch(legacyPageFetchHandler(realPageIndex - 1, pageSize));
      // Otherwise, we are at the first legacy page
      } else {
        // Fetch the last modern page only if it is not already in the Redux store
        if (currentModernPage !== totalModernPages - 1) {
          dispatch(modernPageFetchHandler(totalModernPages - 1, pageSize));
        }
        // Otherwise, the last modern page is already in the Redux store, so no need to fetch it
      }
    }
    prevPageHandler();
  };

  const fetchNextPage = () => {
    if (isModernPage) {
      // If we are not yet at the end of the modern pages, request the next modern page
      if (realPageIndex !== totalModernPages - 1) {
        dispatch(modernPageFetchHandler(realPageIndex + 1, pageSize));
      // Else, we are at the end of the modern pages, so we need to display the first legacy page
      } else {
        // Fetch the fist legacy page only if it is not already in the Redux store
        if (currentLegacyPage !== 0) {
          dispatch(legacyPageFetchHandler(0, pageSize));
        }
        // else the legacy page data is already in the Redux store, so no need to fetch it
      }
    } else {
      // If we are not yet at the end of the legacy pages, request the next legacy page
      if (realPageIndex !== totalLegacyPages - 1) {
        dispatch(legacyPageFetchHandler(realPageIndex + 1, pageSize));
      }
    }
    nextPageHandler();
  };

  return (
    <div className="pagination-div">
      <Pagination className="gcotg-pagination" size="sm">
        <Pagination.Prev
          onClick={() => fetchPrevPage()}
          disabled={currentPage === 0}
        />
        <p className="pagination-description">
          Page {currentPage + 1} of {totalModernPages + totalLegacyPages}
        </p>
        <Pagination.Next
          onClick={() => fetchNextPage()}
          disabled={currentPage === totalModernPages + totalLegacyPages - 1}
        />
      </Pagination>
    </div>
  );
};

export default CombinedOffersPaginationComponent;